import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useState } from "react";

import { installerApi } from "../api/installerApi";

import type {
  CreateReleaseData,
  PaginationParams,
  UpdateReleaseData,
} from "../types";

export const useInstallerReleases = (params?: PaginationParams) => {
  return useQuery({
    queryKey: ["installer-releases", params],

    queryFn: () => installerApi.getReleases(params),
  });
};

export const useInstallerRelease = (id: string) => {
  return useQuery({
    queryKey: ["installer-release", id],

    queryFn: () => installerApi.getRelease(id),

    enabled: !!id,
  });
};

export const useInstallerStats = () => {
  return useQuery({
    queryKey: ["installer-stats"],

    queryFn: () => installerApi.getStats(),
  });
};

export const useCreateRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReleaseData) => installerApi.createRelease(data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["installer-releases"] });

      queryClient.invalidateQueries({ queryKey: ["installer-stats"] });
    },
  });
};

export const useCreateReleaseWithProgress = () => {
  const queryClient = useQueryClient();

  const [uploadProgress, setUploadProgress] = useState(0);

  const mutation = useMutation({
    mutationFn: (data: CreateReleaseData) =>
      installerApi.createRelease(data, setUploadProgress),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["installer-releases"] });

      queryClient.invalidateQueries({ queryKey: ["installer-stats"] });
    },

    onSettled: () => {
      setUploadProgress(0);
    },
  });

  return {
    ...mutation,

    uploadProgress,

    resetProgress: () => setUploadProgress(0),
  };
};

export const useUpdateRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReleaseData }) =>
      installerApi.updateRelease(id, data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["installer-releases"] });

      queryClient.invalidateQueries({ queryKey: ["installer-stats"] });
    },
  });
};

export const useDeleteRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => installerApi.deleteRelease(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["installer-releases"] });

      queryClient.invalidateQueries({ queryKey: ["installer-stats"] });
    },
  });
};

export const usePublishRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => installerApi.publishRelease(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["installer-releases"] });

      queryClient.invalidateQueries({ queryKey: ["installer-stats"] });
    },
  });
};

export const useArchiveRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => installerApi.archiveRelease(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["installer-releases"] });

      queryClient.invalidateQueries({ queryKey: ["installer-stats"] });
    },
  });
};
